import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Services() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newService, setNewService] = useState({
    name: '',
    category_id: '',
    description: '',
    duration_min: 30,
    price: 0,
    discount_price: '',
    color_code: '#ec4899',
    requires_patch_test: false
  });
  const [newCategory, setNewCategory] = useState({
    name: '',
    color_code: '#ec4899'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const [servicesRes, categoriesRes] = await Promise.all([
        axios.get('/api/services', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/categories', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Fetch data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetServiceForm = () => setNewService({
    name: '',
    category_id: '',
    description: '',
    duration_min: 30,
    price: 0,
    discount_price: '',
    color_code: '#ec4899',
    requires_patch_test: false
  });

  const handleOpenAddServiceModal = () => {
    setEditingService(null);
    resetServiceForm();
    setShowModal(true);
  };

  const handleOpenEditServiceModal = (service, e) => {
    if (e) e.stopPropagation();
    setEditingService(service);
    setNewService({
      name: service.name || '',
      category_id: service.category_id || '',
      description: service.description || '',
      duration_min: service.duration_min || 30,
      price: service.price || 0,
      discount_price: service.discount_price || '',
      color_code: service.color_code || '#ec4899',
      requires_patch_test: !!service.requires_patch_test
    });
    setShowModal(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('crm_token');
      if (editingService) {
        await axios.put(`/api/services/${editingService.id}`, newService, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/services', newService, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingService(null);
      resetServiceForm();
      fetchData();
    } catch (error) {
      alert(editingService ? 'Hizmet güncellenemedi' : 'Hizmet eklenemedi');
    }
  };

  const handleDeleteService = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Bu hizmeti silmek (pasifleştirmek) istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.delete(`/api/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Hizmet silinemedi');
    }
  };

  const handleOpenAddCategoryModal = () => {
    setEditingCategory(null);
    setNewCategory({ name: '', color_code: '#ec4899' });
    setShowCategoryModal(true);
  };

  const handleOpenEditCategoryModal = (category, e) => {
    if (e) e.stopPropagation();
    setEditingCategory(category);
    setNewCategory({ name: category.name || '', color_code: category.color_code || '#ec4899' });
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('crm_token');
      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory.id}`, newCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/categories', newCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setNewCategory({ name: '', color_code: '#ec4899' });
      fetchData();
    } catch (error) {
      alert(editingCategory ? 'Kategori güncellenemedi' : 'Kategori eklenemedi');
    }
  };

  const handleDeleteCategory = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.delete(`/api/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Kategori silinemedi');
    }
  };

  // Gercek kategoriler hizmeti olmasa bile HER ZAMAN gosterilir - aksi halde
  // az once olusturulan veya son hizmeti silinen bos bir kategori listeden
  // tamamen kaybolur, kullanici onu duzenleme/silme butonuna hic erisemezdi.
  // "Kategorisiz" sozde-grubu ise sadece gercekten hizmeti varsa (veya hic
  // kategori yoksa, sayfa bomboş kalmasin diye) gosterilir.
  const groupedServices = categories.map(cat => ({
    ...cat,
    services: services.filter(s => s.category_id === cat.id)
  })).concat({
    id: null,
    name: 'Kategorisiz',
    color_code: '#6b7280',
    services: services.filter(s => !s.category_id)
  }).filter(c => c.id !== null || c.services.length > 0 || !categories.length);

  if (loading) return <div>Yükleniyor...</div>;

  return (
    <div>
      <div className="header">
        <h1 className="page-title">Hizmetler</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={handleOpenAddCategoryModal}>
            + Yeni Kategori
          </button>
          <button className="btn btn-primary" onClick={handleOpenAddServiceModal}>
            + Yeni Hizmet
          </button>
        </div>
      </div>

      {groupedServices.map(category => (
        <div key={category.id || 'uncategorized'} className="card mb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{
              width: 16,
              height: 16,
              borderRadius: '4px',
              background: category.color_code
            }} />
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>{category.name}</h3>
            <span className="text-muted">({category.services.length})</span>
            {category.id && (
              <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                <button
                  className="btn btn-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tertiary)', cursor: 'pointer' }}
                  onClick={(e) => handleOpenEditCategoryModal(category, e)}
                  title="Kategoriyi Düzenle"
                >
                  ✏️
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', cursor: 'pointer' }}
                  onClick={(e) => handleDeleteCategory(category.id, e)}
                  title="Kategoriyi Sil"
                >
                  🗑️
                </button>
              </div>
            )}
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Hizmet</th>
                <th>Süre</th>
                <th>Fiyat</th>
                <th>İndirimli</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {category.services.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                    Bu kategoride henüz hizmet yok
                  </td>
                </tr>
              )}
              {category.services.map(service => (
                <tr key={service.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: service.color_code || category.color_code
                      }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{service.name}</div>
                        {service.description && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>{service.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{service.duration_min} dk</td>
                  <td>{parseFloat(service.price).toLocaleString('tr-TR')} ₺</td>
                  <td>
                    {service.discount_price ? (
                      <span style={{ color: 'var(--success)', fontWeight: 500 }}>
                        {parseFloat(service.discount_price).toLocaleString('tr-TR')} ₺
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    {service.requires_patch_test && (
                      <span className="badge badge-pending">Alerji Testi</span>
                    )}
                    {!service.is_active && (
                      <span className="badge badge-cancelled">Pasif</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tertiary)', cursor: 'pointer' }}
                        onClick={(e) => handleOpenEditServiceModal(service, e)}
                        title="Düzenle"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', cursor: 'pointer' }}
                        onClick={(e) => handleDeleteService(service.id, e)}
                        title="Sil"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingService(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet'}</h3>
              <button onClick={() => { setShowModal(false); setEditingService(null); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>

            <form onSubmit={handleServiceSubmit}>
              <div className="form-group">
                <label className="form-label">Hizmet Adı *</label>
                <input
                  type="text"
                  className="input"
                  value={newService.name}
                  onChange={e => setNewService({ ...newService, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kategori</label>
                <select
                  className="select"
                  value={newService.category_id}
                  onChange={e => setNewService({ ...newService, category_id: e.target.value })}
                >
                  <option value="">Seçiniz</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Süre (dk) *</label>
                  <input
                    type="number"
                    className="input"
                    value={newService.duration_min}
                    onChange={e => setNewService({ ...newService, duration_min: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Fiyat (₺) *</label>
                  <input
                    type="number"
                    className="input"
                    value={newService.price}
                    onChange={e => setNewService({ ...newService, price: e.target.value })}
                    step="0.01"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">İndirimli Fiyat</label>
                <input
                  type="number"
                  className="input"
                  value={newService.discount_price}
                  onChange={e => setNewService({ ...newService, discount_price: e.target.value })}
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <textarea
                  className="input"
                  value={newService.description}
                  onChange={e => setNewService({ ...newService, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={newService.requires_patch_test}
                    onChange={e => setNewService({ ...newService, requires_patch_test: e.target.checked })}
                  />
                  Alerji testi gerektirir
                </label>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                {editingService ? 'Hizmeti Güncelle' : 'Hizmet Ekle'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => { setShowCategoryModal(false); setEditingCategory(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}</h3>
              <button onClick={() => { setShowCategoryModal(false); setEditingCategory(null); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>

            <form onSubmit={handleCategorySubmit}>
              <div className="form-group">
                <label className="form-label">Kategori Adı *</label>
                <input
                  type="text"
                  className="input"
                  value={newCategory.name}
                  onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Renk Kodu</label>
                <input
                  type="color"
                  className="input"
                  value={newCategory.color_code}
                  onChange={e => setNewCategory({ ...newCategory, color_code: e.target.value })}
                  style={{ height: 40 }}
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                {editingCategory ? 'Kategoriyi Güncelle' : 'Kategori Ekle'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;
